import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { PurchaseChannel } from '@ridango/core/lib/enums/purchase-channel.enum';
import { getPurchaseChannel } from '@ridango/core/lib/utils/channel.util';
import Env from '@ridango/core/env';
import { Observable } from 'rxjs';
import { Organization, CustomerCreditInfo, TripPurpose } from './corporate-employees.models';

export enum SmsResponseStatus {
    APPROVED = 'APPROVED',
    PENDING = 'PENDING',
    CANCELED = 'CANCELED'
}

@Injectable({
    providedIn: 'root'
})
export class CorporateEmployeesService {
    private baseUrl: string;

    constructor(
        @Inject('ENV') private env: Env,
        private http: HttpClient,
        private translateService: TranslateService
    ) {
        this.baseUrl = `${env.CORE_API_URL}/customer/v3/region/${env.REGION_ID}/corporate-employees`;
    }

    public sendSms(countryCallingCode: string, phoneNumber: string) {
        const url = `${this.baseUrl}/sms-verification/send`;

        return this.http.post<{ phone_number: string; status: SmsResponseStatus }>(url, {
            country_calling_code: countryCallingCode,
            phone_number: phoneNumber,
            tenant_name: this.env.WMB?.TENANT_NAME || 'Awesome transport mobile app',
            lang: this.translateService.currentLang
        });
    }

    public getCustomerCreditInfo(customerAccountId: number | string) {
        const channel: PurchaseChannel = getPurchaseChannel(this.env);
        const url = `${this.env.WMB.WMB_PUBLIC_API}/v1/afc-customers/customer-accounts/channel/${channel}/${customerAccountId}/monthly-usage`;
        return this.http.get<CustomerCreditInfo>(url);
    }

    public checkSms(countryCallingCode: string, phoneNumber: string, smsCode: string) {
        const url = `${this.baseUrl}/sms-verification/check`;

        return this.http.post<{ phone_number: string; status: SmsResponseStatus }>(url, {
            country_calling_code: countryCallingCode,
            phone_number: phoneNumber,
            sms_code: smsCode
        });
    }

    public getCountryCallingCodes(): Observable<string[]> {
        const url = `${this.baseUrl}/country-calling-codes`;

        return this.http.get<string[]>(url);
    }

    public getEmployeeOrganizations(): Observable<Organization[]> {
        const url = `${this.env.CORE_API_URL}/customer/v3/region/${this.env.REGION_ID}/corporate-employee-organizations`;

        return this.http.get<Organization[]>(url);
    }

    public getOrganizationPurposes(corporateEmployeeId: number): Observable<TripPurpose[]> {
        const url = `${this.baseUrl}/${corporateEmployeeId}/trip-purposes`;

        return this.http.get<TripPurpose[]>(url);
    }

    public getTripPurposes(customerAccountId?: number | string | null): Observable<TripPurpose[]> {
        const channel: PurchaseChannel = getPurchaseChannel(this.env);
        const url = `${this.env.WMB.WMB_PUBLIC_API}/v1/cart/channel/${channel}/trip-purposes`;
        const params = new HttpParams();

        if (customerAccountId) {
            params.set('customerAccountId', customerAccountId);
        }

        return this.http.get<TripPurpose[]>(url, { params });
    }
}
